import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { User, PlanTier } from '../types';
import { Edit2, Save, X, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

export function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [currentPlan, setCurrentPlan] = useState<PlanTier | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    companyName: '',
    password: '',
  });
  const [availablePlans, setAvailablePlans] = useState<PlanTier[]>([]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchUserData();
    fetchAvailablePlans();
  }, [id, isAdmin]);

  const fetchUserData = async () => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .single();

      if (profileError) throw profileError;

      const { data: subscription, error: subError } = await supabase
        .from('subscriptions')
        .select(`
          *,
          plan:plan_id (*)
        `)
        .eq('user_id', id)
        .eq('status', 'active')
        .maybeSingle();

      if (subError) throw subError;

      setUser(profile);
      setCurrentPlan(subscription?.plan || null);
      setEditForm({
        name: profile.name || '',
        email: profile.email,
        companyName: profile.company_name || '',
        password: '',
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Failed to load user data');
    }
  };

  const fetchAvailablePlans = async () => {
    try {
      const { data, error } = await supabase
        .from('plan_tiers')
        .select('*')
        .eq('is_active', true)
        .order('price');

      if (error) throw error;
      setAvailablePlans(data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          name: editForm.name,
          email: editForm.email,
          company_name: editForm.companyName,
        })
        .eq('id', id);

      if (profileError) throw profileError;

      // Update auth email if changed
      if (editForm.email !== user?.email) {
        const { error: authError } = await supabase.auth.admin.updateUserById(
          id!,
          { email: editForm.email }
        );
        if (authError) throw authError;
      }

      // Update password if provided
      if (editForm.password) {
        const { error: passwordError } = await supabase.auth.admin.updateUserById(
          id!,
          { password: editForm.password }
        );
        if (passwordError) throw passwordError;
      }

      toast.success('User updated successfully');
      setIsEditing(false);
      fetchUserData();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async (planId: string) => {
    try {
      setLoading(true);

      // Cancel current subscription in Stripe if exists
      if (currentPlan) {
        const response = await fetch('/functions/v1/cancel-subscription', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({ 
            userId: id,
            subscriptionId: currentPlan.id 
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to cancel subscription');
        }
      }

      // Update subscription in database
      const { error: subError } = await supabase
        .from('subscriptions')
        .upsert({
          user_id: id,
          plan_id: planId,
          status: 'active',
          current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        });

      if (subError) throw subError;

      toast.success('Plan updated successfully');
      fetchUserData();
    } catch (error) {
      console.error('Error updating plan:', error);
      toast.error('Failed to update plan');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">User Profile</h2>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center text-blue-600 hover:text-blue-700"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex items-center text-green-600 hover:text-green-700"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex items-center text-gray-600 hover:text-gray-700"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          {isEditing ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name</label>
                <input
                  type="text"
                  value={editForm.companyName}
                  onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  New Password (leave blank to keep unchanged)
                </label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-500">Name</label>
                <p className="mt-1 text-sm text-gray-900">{user.name || 'Not set'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Company Name</label>
                <p className="mt-1 text-sm text-gray-900">{user.company_name || 'Not set'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <p className="mt-1 text-sm text-gray-900">{user.email}</p>
              </div>
            </>
          )}

          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {availablePlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`p-4 rounded-lg border ${
                    currentPlan?.id === plan.id
                      ? 'border-blue-500 ring-2 ring-blue-500'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="text-base font-medium text-gray-900">{plan.name}</h4>
                      <p className="text-sm text-gray-500">${plan.price}/{plan.interval}</p>
                    </div>
                    {currentPlan?.id !== plan.id && (
                      <button
                        onClick={() => handlePlanChange(plan.id)}
                        disabled={loading}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        Switch to this plan
                      </button>
                    )}
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>Max Videos: {plan.features.maxVideos}</li>
                    <li>Max Minutes: {plan.features.maxMinutes}</li>
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}